'use client'

import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'

type UploadedAudio = {
    name: string
    type: string
    sizeInMb: string
    url: string
}

const minPlaybackRate = 0.5
const maxPlaybackRate = 2
const defaultPlaybackRate = 1
const quickSpeedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2]

function formatTime(seconds: number) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '00:00'
    }

    const minutes = Math.floor(seconds / 60)
    const remainSeconds = Math.floor(seconds % 60)

    return `${String(minutes).padStart(2, '0')}:${String(remainSeconds).padStart(2, '0')}`
}

function toMb(bytes: number) {
    return (bytes / 1024 / 1024).toFixed(2)
}

export default function AudioCustomClient() {
    const [uploadedAudio, setUploadedAudio] = useState<UploadedAudio | null>(null)
    const [playbackRate, setPlaybackRate] = useState(defaultPlaybackRate)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [audioError, setAudioError] = useState('')
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const objectUrlRef = useRef<string | null>(null)

    const playbackRateLabel = useMemo(() => `${playbackRate.toFixed(2)}x`, [playbackRate])

    useEffect(() => {
        if (!audioRef.current) {
            return
        }
        audioRef.current.playbackRate = playbackRate
    }, [playbackRate])

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current)
                objectUrlRef.current = null
            }
        }
    }, [])

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]

        if (!selectedFile) {
            return
        }

        if (selectedFile.type && !selectedFile.type.startsWith('audio/')) {
            setAudioError('File không hợp lệ. Vui lòng chọn file audio.')
            return
        }

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = null
        }

        const objectUrl = URL.createObjectURL(selectedFile)
        objectUrlRef.current = objectUrl

        setUploadedAudio({
            name: selectedFile.name,
            type: selectedFile.type || 'Unknown',
            sizeInMb: toMb(selectedFile.size),
            url: objectUrl
        })
        setAudioError('')
        setCurrentTime(0)
        setDuration(0)
        setIsPlaying(false)
    }

    return (
        <main className='mx-auto w-full max-w-(--breakpoint-md) px-4 py-6 sm:px-6 sm:py-8'>
            <div className='mb-6 text-center sm:mb-8'>
                <h1 className='text-2xl font-bold text-foreground sm:text-3xl'>Tùy chỉnh tốc độ audio</h1>
                <p className='mt-2 text-sm text-muted-foreground sm:text-base'>
                    Tải file audio từ máy lên, sau đó thay đổi tốc độ để luyện nghe theo nhịp bạn muốn.
                </p>
            </div>

            <section className='rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5'>
                <label htmlFor='custom-audio-file' className='block text-sm font-medium text-foreground'>
                    Chọn file audio
                </label>
                <input
                    id='custom-audio-file'
                    type='file'
                    accept='audio/*'
                    onChange={handleFileChange}
                    className='mt-2 block w-full cursor-pointer rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-foreground hover:file:bg-accent'
                />
                <p className='mt-2 text-xs text-muted-foreground'>Hỗ trợ các định dạng audio phổ biến như mp3, wav, m4a.</p>
            </section>

            {audioError && (
                <p className='mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive'>
                    {audioError}
                </p>
            )}

            {uploadedAudio ? (
                <section className='mt-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                        <p className='max-w-full truncate text-sm font-semibold text-foreground' title={uploadedAudio.name}>
                            {uploadedAudio.name}
                        </p>
                        <span className='text-xs text-muted-foreground'>
                            {uploadedAudio.sizeInMb} MB - {uploadedAudio.type}
                        </span>
                    </div>

                    <div className='mt-4 space-y-3'>
                        <div className='flex items-center justify-between gap-2 text-sm text-foreground'>
                            <label htmlFor='playback-rate'>Tốc độ phát</label>
                            <span className='rounded-md bg-secondary px-2 py-1 text-xs font-semibold text-secondary-foreground'>
                                {playbackRateLabel}
                            </span>
                        </div>
                        <input
                            id='playback-rate'
                            type='range'
                            min={minPlaybackRate}
                            max={maxPlaybackRate}
                            step={0.05}
                            value={playbackRate}
                            onChange={(event) => setPlaybackRate(Number(event.target.value))}
                            className='w-full accent-primary'
                        />
                        <div className='flex flex-wrap gap-2'>
                            {quickSpeedOptions.map((speed) => (
                                <Button
                                    key={speed}
                                    type='button'
                                    onClick={() => setPlaybackRate(speed)}
                                    className={cn(
                                        'rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors',
                                        playbackRate === speed
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {speed.toFixed(2)}x
                                </Button>
                            ))}
                        </div>
                    </div>

                    <audio
                        ref={audioRef}
                        src={uploadedAudio.url}
                        controls
                        className='mt-4 w-full'
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
                        onLoadedMetadata={(event) => {
                            const nextDuration = event.currentTarget.duration
                            setDuration(Number.isFinite(nextDuration) ? nextDuration : 0)
                            event.currentTarget.playbackRate = playbackRate
                        }}
                    />

                    <div className='mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground'>
                        <span>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                        <span>{isPlaying ? 'Đang phát' : 'Tạm dừng'}</span>
                    </div>
                </section>
            ) : (
                <p className='mt-4 rounded-xl border border-dashed border-border bg-muted/40 px-4 py-5 text-sm text-muted-foreground'>
                    Chưa có audio nào được tải lên.
                </p>
            )}
        </main>
    )
}
